import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { PktrAppMenuService } from '@prospektr/pktr-app-menu';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthorizeService } from 'src/app/_core/access-control/authorize.service';
import { FavoriteModalComponent } from 'src/app/_shared/component/favorite-modal/favorite-modal.component';
import { Json, PaginatedResponse } from 'src/app/_shared/interface/common.interface';
import { FavoriteGroup } from 'src/app/_shared/interface/favorite.interface';
import { FavoriteService } from 'src/app/_shared/service/favorite.service';
import { FavoriteState } from 'src/app/_shared/state/favorite.state';
import { SubscriptionState } from 'src/app/_shared/state/subscription.state';
import { UserState } from 'src/app/_shared/state/user.state';
import { AppState } from 'src/app/app.state';
import { ConfirmDialogComponent } from 'src/app/shared/modules/alerts/confirm-dialog/confirm-dialog.component';
import { SubscriptionPlanService } from 'src/app/shared/service/subscription-plan.service';
import { ValidatorsService } from 'src/app/shared/service/validators.service';
import MESSAGE from 'src/assets/resource/strings.json';
import { FollowedPropertiesService } from '../../service/followed-properties.service';
import { FollowedPropertiesState } from '../../state/followed-properties.state';

@Component({
  selector: 'app-followed-properties',
  templateUrl: './followed-properties.component.html',
  styleUrls: ['./followed-properties.component.scss'],
})
export class FollowedPropertiesComponent implements OnInit, OnDestroy {
  @ViewChild('groupPaginator') groupPaginator: MatPaginator;
  @ViewChild('propertyPaginator') propertyPaginator: MatPaginator;

  @Output() clickMenuEvent = new EventEmitter<any>();

  // Flags
  isExternalExportEnabled = false;
  isFavoriteGroupLoading: boolean;
  isGroupPropertyLoading: boolean;

  action: string;
  actions: any = ['copy', 'move'];
  activeFavoriteGroup: Json;
  activeGroupProperties: PaginatedResponse;
  activeGroupPropertyIdList: Array<string> = [];
  companyName = '';
  createWatchListSheetActive = false;
  dialogRef: any;
  duration: number;
  exportingInProgress: any = {};
  favLimit: number;
  favoriteGroupDataSource: MatTableDataSource<any>;
  favoriteGroupDataSource$: Observable<any>;
  favoritePropertyFetchParams = { limit: 9, offset: 0 };
  favUsageCount: number;
  followGroupNameList: Array<FavoriteGroup>;
  groupNamePattern = /(?:[A-Za-z0-9]+)(?:[A-Za-z0-9 _<>+-@#$%^]*)$/;
  isGroupNameUpdateInProgress: any = {};
  searchPropertyInput: string;
  selectedActiveGroupPropertyIdList: Array<string> = [];
  selectedPropertyForCopy: any[];
  stringRes: any = MESSAGE;

  onDestroyNotifier$ = new Subject();

  constructor(
    private appState: AppState,
    private authorizeService: AuthorizeService,
    private dialog: MatDialog,
    private favoriteService: FavoriteService,
    private followService: FollowedPropertiesService,
    private router: Router,
    private snackBar: MatSnackBar,
    private subscriptionService: SubscriptionPlanService,
    private subscriptionState: SubscriptionState,
    private userState: UserState,
    public favoriteState: FavoriteState,
    public _pktrAppMenuService: PktrAppMenuService,
    public followedPropertiesState: FollowedPropertiesState,
    public breakpointService: ValidatorsService
  ) { }

  ngOnInit() {
    this.userState.userDetail$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      if (res) {
        this.followService.listFavoriteGroup().subscribe(({ isLoading }) => {
          this.isFavoriteGroupLoading = isLoading;
        });
      }
    });

    this.subscriptionState.planUsage.pipe(takeUntil(this.onDestroyNotifier$)).subscribe(
      (res) => {
        this.getPlanUsageCount(res);
      },
      (err) => {
        console.log('err:', err);
      }
    );

    this.appState.companyInfo$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res && res.isExternalExportEnabled) {
        this.isExternalExportEnabled = res.isExternalExportEnabled ? true : false;
        this.companyName = res.name;
      }
    });

    this.followedPropertiesState.openCreateWatchListSheet$
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((status) => {
        this.createWatchListSheetActive = status;
      });

    this.favoriteState.followGroupList$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      this.favoriteGroupDataSource = new MatTableDataSource<FavoriteGroup>(res);
      this.favoriteGroupDataSource.paginator = this.groupPaginator;
      this.favoriteGroupDataSource$ = this.favoriteGroupDataSource.connect();
    });

    this.followedPropertiesState.favoritePropertiesByGroup$.subscribe((response) => {
      if (response?.totalCount) {
        this.activeGroupProperties = response;
        this.propertyPaginator.length = response.totalCount;
        this.propertyPaginator.pageIndex =
          this.favoritePropertyFetchParams.offset / this.favoritePropertyFetchParams.limit;
      }
    });

    this.followedPropertiesState.activeFavoriteGroup$.subscribe((response) => {
      this.activeFavoriteGroup = response;
    });
  }

  ngOnDestroy() {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
    this.followedPropertiesState.activeFavoriteGroupValue = null;
    this.followedPropertiesState.favoritePropertiesByGroupValue = null;
  }

  pageChange(event) {
    this.favoritePropertyFetchParams.limit = event.pageSize;
    this.favoritePropertyFetchParams.offset = event.pageIndex * event.pageSize;
    this.getPropertyList(this.followedPropertiesState.activeFavoriteGroupValue);
  }

  getPlanUsageCount(data: any) {
    if (data) {
      this.favUsageCount = data.favoriteUsage || 0;
      this.duration = data.duration || 0;
      this.favLimit = data.favoriteLimit || 0;
    }
  }

  updateGroupName(group: FavoriteGroup, index: number) {
    if (group.newName && group.newName.trim() && this.groupNamePattern.test(group.newName)) {
      this.isGroupNameUpdateInProgress[index] = true;
      if (group.newName === group.name) {
        this.isGroupNameUpdateInProgress = {};
        group.editGroupName = false;
        group.name = group.newName;
        group.newName = '';
      } else {
        this.followService
          .updateGroupName({
            groupId: group.id,
            name: group.newName,
          })
          .pipe(takeUntil(this.onDestroyNotifier$))
          .subscribe(
            (res) => {
              this.isGroupNameUpdateInProgress = {};
              group.editGroupName = false;
              if (res && res.status == 'success') {
                group.name = group.newName;
                group.newName = '';
                this.openSnackBar(res.message ? res.message : '', 'snackbar-success');
              } else {
                this.openSnackBar(res.message ? res.message : '', 'snackbar-error');
              }
            },
            (err) => {
              this.isGroupNameUpdateInProgress = {};
              this.openSnackBar(err.message ? err.message : 'Error occurred', 'snackbar-error');
            }
          );
      }
    }
  }

  deleteGroup(deleteRecord) {
    if (!this.authorizeService.canAct('favoriteEdit')) {
      return false;
    }
    if (!deleteRecord.id) {
      return false;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '385px',
      disableClose: true,
      hasBackdrop: true,
      data: {
        type: 'deleteFollowGroup',
        message: this.stringRes.PROPERTY_DELETION_FROM_WATCH_LIST_MESSAGE,
        title: 'Do you want to delete?',
        proceedAction: (status, callBack) => {
          if (status) {
            this.favoriteService
              .deleteGroup(deleteRecord)
              .pipe(takeUntil(this.onDestroyNotifier$))
              .subscribe(
                (res: any) => {
                  this.openSnackBar('Watchlist deleted successfully', 'snackbar-success');
                  this.followService.listFavoriteGroup().subscribe(({ isLoading }) => {
                    this.isFavoriteGroupLoading = isLoading;
                  });
                  callBack({ status: true });
                  dialogRef.close();
                },
                () => {
                  this.openSnackBar('Some error occurred', 'snackbar-error');
                }
              );
          } else {
            return false;
          }
        },
        proceedCancelAction: () => {
          dialogRef.close();
        },
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((result) => {
        if (result) {
          this.followGroupNameList = this.followService.curUpdatedGroupNameListAfterDelete(
            this.followGroupNameList,
            deleteRecord.id
          );
        } else {
          return false;
        }
      });
  }

  getPropertyList(favoriteGroup) {
    this.followedPropertiesState.activeFavoriteGroupValue = favoriteGroup;

    this.followService
      .fetchFavoritesByGroup(favoriteGroup.id, this.favoritePropertyFetchParams)
      .subscribe(({ isLoading }) => {
        this.isGroupPropertyLoading = isLoading;
      });

    this.followService.fetchFavoriteIdsByGroup(favoriteGroup.id).subscribe(({ data }) => {
      this.activeGroupPropertyIdList = data;
    });
  }

  unFollowProperty(type, id?) {
    if (!this.authorizeService.canAct('favoriteEdit')) {
      return false;
    }
    const _this = this;
    let propertyIdList = [];
    let message = '';
    if (type == 'single') {
      propertyIdList = [id];
      message = this.stringRes.DELETE_ONE_WATCH_LIST_MESSAGE;
    } else {
      propertyIdList = this.selectedActiveGroupPropertyIdList;
      message = this.stringRes.DELETE_MORE_WATCH_LIST_MESSAGE;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '385px',
      disableClose: true,
      hasBackdrop: true,
      data: {
        type: 'unfollowProperty',
        message: message,
        title: '',
        proceedAction: (status, callBack) => {
          if (status) {
            this.followService
              .deleteFavorite(propertyIdList)
              .pipe(takeUntil(this.onDestroyNotifier$))
              .subscribe(
                (res) => {
                  this.selectedActiveGroupPropertyIdList = [];
                  callBack({ status: true });
                  this.openSnackBar(res.message, 'snackbar-success');

                  this.followService.listFavoriteGroup().subscribe(({ isLoading }) => {
                    this.isFavoriteGroupLoading = isLoading;
                  });

                  this.favoritePropertyFetchParams.offset = 0;
                  this.followService
                    .fetchFavoritesByGroup(_this.activeFavoriteGroup.id as string, this.favoritePropertyFetchParams)
                    .subscribe(({ isLoading }) => {
                      this.isGroupPropertyLoading = isLoading;
                    });

                  this.activeGroupPropertyIdList = this.activeGroupPropertyIdList.filter(
                    (id) => !propertyIdList.includes(id)
                  );
                },
                (err) => {
                  callBack({ status: true });
                  this.openSnackBar(err.message ? err.message : 'Error occurred', 'snackbar-error');
                }
              );
          } else {
            return false;
          }
        },
        proceedCancelAction: () => {
          dialogRef.close();
        },
      },
    });
  }

  goBack() {
    this.activeGroupProperties = null;
    this.followedPropertiesState.favoritePropertiesByGroupValue = null;
    this.followedPropertiesState.activeFavoriteGroupValue = null;
    this.selectedActiveGroupPropertyIdList = [];
  }

  isAllSelected() {
    return this.activeGroupPropertyIdList?.length == this.selectedActiveGroupPropertyIdList?.length;
  }

  toggleSelectAll() {
    if (this.isAllSelected()) {
      this.selectedActiveGroupPropertyIdList = [];
    } else {
      this.selectedActiveGroupPropertyIdList = this.activeGroupPropertyIdList;
    }
  }

  toggleSelect(property) {
    if (property.selected) {
      this.selectedActiveGroupPropertyIdList.push(property.id);
    } else {
      this.selectedActiveGroupPropertyIdList = this.selectedActiveGroupPropertyIdList.filter((id) => {
        return id != property.id;
      });
    }
  }

  readyToExport(from, groupId?, groupName?) {
    if (!this.authorizeService.canAct('favoriteEdit')) {
      return false;
    }
    this.exportingInProgress[from + (groupId ? groupId : '')] = true;
    let inputParams;
    if (from === 'group') {
      inputParams = {
        type: 'All',
        groupId: groupId,
      };
    } else {
      inputParams = {
        type: 'Selected',
        favoriteList: this.selectedActiveGroupPropertyIdList,
      };
    }
    this.followService
      .exportFavoritesToCSV(inputParams)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res) => {
        if (res && res.status && res.status === 'error') {
          this.openSnackBar(res.Message ? res.Message : '', 'snackbar-error');
          this.selectedActiveGroupPropertyIdList = [];
          this.exportingInProgress[from + (groupId ? groupId : '')] = false;
        } else if (res && res.Data) {
          let delimitedText = res.Data;
          let csvName = res.csvName ? res.csvName + '.csv' : 'followedPropertiesList.csv';
          let csvData = new Blob([delimitedText], {
            type: 'text/csv;charset=utf-8;',
          });
          let csvURL = window.URL.createObjectURL(csvData);
          let downloadFile = document.createElement('a');
          downloadFile.href = csvURL;
          downloadFile.setAttribute('download', csvName);
          downloadFile.click();
          this.selectedActiveGroupPropertyIdList = [];
          this.exportingInProgress[from + (groupId ? groupId : '')] = false;
        }
      });
  }

  applyFilter(filterValue: string, type) {
    this.favoriteGroupDataSource.filter = filterValue.trim().toLowerCase();
  }

  goToHome() {
    this.router.navigate(['workspace']);
  }

  openSnackBar(message, style) {
    if (message) {
      this.snackBar.open(message, '', {
        duration: 3000,
        panelClass: [style ? style : ''],
        verticalPosition: 'top',
      });
    }
  }

  manageDialogBox(groupResult) {
    this.searchPropertyInput = null;
    this.followGroupNameList = this.followService.curUpdatedGroupNameListAfterAdding(
      this.followGroupNameList,
      groupResult
    );
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  openPopUp(actionToBeDone) {
    this.breakpointService
      .isMobileOrTablet()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((isMobileOrTablet) => {
        if (isMobileOrTablet) {
          this._pktrAppMenuService.close(true);
        }

      });

    if (!this.authorizeService.canAct('favoriteAdd')) {
      return false;
    }
    if (actionToBeDone === 'createPlusAddMultiplePropertyToGroup' && !this.selectedActiveGroupPropertyIdList.length) {
      this.openSnackBar('Please select any properties from the list', 'snackbar-error');
      return false;
    }
    this.followedPropertiesState.openCreateWatchListSheetValue = true;
    this.action = actionToBeDone;
  }

  resetCopyOptions(processType) {
    if (processType == 'COPY') {
      this.subscriptionService.getSubscriptionPlan();
    }
    this.selectedActiveGroupPropertyIdList = [];
    this.favoritePropertyFetchParams.offset = 0;
    this.followService
      .fetchFavoritesByGroup(this.activeFavoriteGroup.id as string, this.favoritePropertyFetchParams)
      .subscribe(({ isLoading }) => {
        this.isGroupPropertyLoading = isLoading;
      });
    this.followService.listFavoriteGroup().subscribe(({ isLoading }) => {
      this.isFavoriteGroupLoading = isLoading;
    });
  }

  processToAnotherGroup(dataType, processType: 'move' | 'copy', curRow?) {
    if (!this.authorizeService.canAct('favoriteEdit')) {
      return false;
    }
    if (!this.selectedActiveGroupPropertyIdList.length && dataType === 'multiple') {
      this.openSnackBar('Please select any properties from the list', 'snackbar-error');
      return false;
    } else {
      const groupList = JSON.parse(JSON.stringify(this.favoriteGroupDataSource.data)).filter(
        (el) => el.id !== this.followedPropertiesState.activeFavoriteGroupValue.id
      );

      if (groupList && groupList.length) {
        const followPropertyList =
          dataType === 'single' ? [curRow.id] : JSON.parse(JSON.stringify(this.selectedActiveGroupPropertyIdList));

        const dialogRef = this.dialog.open(FavoriteModalComponent, {
          data: {
            action: processType,
            dataType: 'favorite',
            dataList: followPropertyList,
          },
        });

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.onDestroyNotifier$))
          .subscribe((result) => {
            this.resetCopyOptions(processType);
          });
      } else {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          width: '385px',
          disableClose: true,
          hasBackdrop: true,
          data: {
            type: 'addNewFollowGroup',
            message: this.stringRes.WANT_TO_ADD_NEW_WATCH_LIST,
            title: '',
          },
        });
        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.onDestroyNotifier$))
          .subscribe((result) => {
            if (result) {
              if (dataType === 'single') {
                this.selectedPropertyForCopy = [curRow.id];
              }
              this.openPopUp(
                dataType === 'multiple' ? 'createPlusAddMultiplePropertyToGroup' : 'createPlusAddSinglePropertyToGroup'
              );
            } else {
              return false;
            }
          });
        this.openSnackBar(this.stringRes.NO_COPY_WATCH_LIST, 'snackbar-error');
      }
    }
  }

  goToParentPath() {
    const navigatedTo = this.appState.navigatedFrom;
    navigatedTo
      ? navigatedTo === 'dashboard'
        ? this.clickMenuEvent.emit('dashboard')
        : this.router.navigate([navigatedTo])
      : this.clickMenuEvent.emit('dashboard');
  }

  externalExport(type, idList) {
    this.dialog.open(ConfirmDialogComponent, {
      width: '385px',
      disableClose: true,
      hasBackdrop: true,
      panelClass: 'custom-modalbox',
      data: {
        type: 'exportFollowGroup',
        message: this.stringRes.EXPORT_WATCH_LIST + ` ${this.companyName}?`,
        title: 'Export',
        proceedAction: (status, callBack) => {
          if (status) {
            this.exportingInProgress['group' + (idList[0] ? idList[0] : '')] = true;
            callBack({ status: true });
            this.favoriteService
              .exportFavoriteToExternal(type, idList)
              .pipe(takeUntil(this.onDestroyNotifier$))
              .subscribe(() => {
                this.exportingInProgress['group' + (idList[0] ? idList[0] : '')] = false;
              });
          } else {
            return false;
          }
        },
      },
    });
  }

  enableEditing(element) {
    if (!this.authorizeService.canAct('favoriteEdit')) {
      return false;
    }
    element.editGroupName = true;
    element.newName = element.name;
  }

  closeDialog() {
    this.dialogRef.close();
  }
}
